import React, {useState} from 'react';

import {Button, Icon, Input, Text, addToast, addGenericErrorToast} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {genericMandarkRequest} from 'resources/mandark.resource';

import {usePerformSignupStep} from '../../usePerformSignupStep';
import OnboardingPageLayout from '../OnboardingPageLayout';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface NewParentalConsentStepProps {
  onBack: () => void;
  onSubmit: () => void;
}

export default function NewParentalConsentStep({onBack, onSubmit}: NewParentalConsentStepProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const performSignupStep = usePerformSignupStep();

  const trimmedEmail = email.trim();
  const formatError =
    touched && trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)
      ? 'Please enter a valid email address.'
      : null;
  const displayError = error || formatError;

  const handleSubmit = async () => {
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const userId = sessionStore.getUserId();

      await genericMandarkRequest(
        'patch',
        {resourcePath: ['users_v2', userId]},
        {
          data: {
            type: 'users_v2',
            id: userId,
            attributes: {coppa_consent_email: trimmedEmail}
          }
        }
      );

      await performSignupStep({
        parentalConsent: {
          consentMethod: 'parental_email',
          coppaConsentEmail: trimmedEmail
        }
      });
      addToast({
        message:
          "We've sent your parent an email. Once they approve your request, you can use Albert.",
        title: 'Success!',
        color: 'positive'
      });
      onSubmit();
    } catch (e: any) {
      const detail = e?.response?.body?.errors?.[0]?.detail;
      if (detail) {
        setError(detail);
      } else {
        addGenericErrorToast();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingPageLayout>
      <Button
        color='primary'
        variant='text'
        size='s'
        onClick={onBack}
        className='flex max-w-fit items-center justify-start gap-x1'
      >
        <Icon icon='arrow-left' />
        <Text color='inherit'>Back</Text>
      </Button>

      <div className='flex flex-col gap-x0-half'>
        <Text className='text-xxl font-black'>Request Parental Permission</Text>
        <Text>
          Tell us your parent&apos;s email and we&apos;ll ask them if it&apos;s okay for you to use
          Albert.
        </Text>
      </div>

      <div className='px-x1'>
        <Input
          type='email'
          placeholder='Enter email'
          value={email}
          error={!!displayError}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          onBlur={() => setTouched(true)}
          aria-label='Parent email'
        />
        {displayError && (
          <Text size='s' color='negative' className='mt-x0-half'>
            {displayError}
          </Text>
        )}
      </div>

      <Button
        className='w-full'
        color='primary'
        size='l'
        roundness='pill'
        disabled={!trimmedEmail || !!displayError || isSubmitting}
        onClick={handleSubmit}
      >
        <Text color='primary-inverse-static'>{isSubmitting ? 'Sending...' : 'Submit'}</Text>
      </Button>
    </OnboardingPageLayout>
  );
}
